/**
 * Carte opérationnelle terrain — passages / tournées / anomalies GPS (données GET /passages, /tournees).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { AlertTriangle, Layers, RefreshCw } from "lucide-react";
import { toast } from "react-toastify";
import { api } from "../../../lib/axios";
import type { PassageApiItem, TourneeApiItem } from "../planificateur/planningTypes";
import { clientLabel } from "../planificateur/planningDashboardUtils";

delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png",
});

/** Lubumbashi — centre par défaut si aucune coordonnée */
const DEFAULT_CENTER: [number, number] = [-11.6642, 27.4794];

export default function TerrainCartePage(): React.JSX.Element {
  const [passages, setPassages] = useState<PassageApiItem[]>([]);
  const [tournees, setTournees] = useState<TourneeApiItem[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, tRes] = await Promise.all([
        api.get<{ items?: PassageApiItem[] }>("/passages", { params: { limit: 100, page: 1 } }),
        api.get<{ items?: TourneeApiItem[] }>("/tournees", { params: { limit: 100, page: 1 } }),
      ]);
      setPassages(Array.isArray(pRes.data?.items) ? pRes.data.items : []);
      setTournees(Array.isArray(tRes.data?.items) ? tRes.data.items : []);
    } catch {
      toast.error("Impossible de charger passages ou tournées.");
      setPassages([]);
      setTournees([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const markers = useMemo(() => {
    const out: {
      key: string;
      position: [number, number];
      title: string;
      detail: string;
      color: string;
      kind: "passage" | "tournee";
    }[] = [];

    for (const p of passages) {
      const lat = p.startLatitude ?? p.endLatitude;
      const lng = p.startLongitude ?? p.endLongitude;
      if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) continue;
      const st = p.statut?.toUpperCase() ?? "";
      const suspect = Boolean(p.isSuspect);
      const executed =
        Boolean(p.dateRealisee) ||
        Boolean(p.startedAt || p.endedAt) ||
        String(p.executionStatus ?? "").toUpperCase() === "REALISE" ||
        ["EFFECTUE", "PARTIEL", "EN_ATTENTE_VALIDATION", "CLOSED_WITH_ANOMALIES", "NON_EFFECTUE"].includes(st);
      const color = suspect ? "#f43f5e" : executed ? "#22c55e" : "#0ea5e9";
      out.push({
        key: `p-${p.id}`,
        position: [lat, lng],
        title: `Passage #${p.id}`,
        detail: `${clientLabel(p)} · ${p.statut}${suspect ? " · GPS suspect" : ""}`,
        color,
        kind: "passage",
      });
    }

    return out;
  }, [passages]);

  const stats = useMemo(() => {
    const hasGps = (p: PassageApiItem): boolean => {
      const lat = p.startLatitude ?? p.endLatitude;
      const lng = p.startLongitude ?? p.endLongitude;
      return lat != null && lng != null && !Number.isNaN(lat) && !Number.isNaN(lng);
    };
    const executedTerrain = (p: PassageApiItem): boolean => {
      const st = p.statut?.toUpperCase() ?? "";
      if (p.dateRealisee) return true;
      if (p.startedAt || p.endedAt) return true;
      if (String(p.executionStatus ?? "").toUpperCase() === "REALISE") return true;
      return ["EFFECTUE", "PARTIEL", "EN_ATTENTE_VALIDATION", "CLOSED_WITH_ANOMALIES", "NON_EFFECTUE"].includes(st);
    };

    const totalPassages = passages.length;
    const avecCoord = passages.filter(hasGps).length;
    const sansCoord = totalPassages - avecCoord;
    const executesAvecCoord = passages.filter((p) => hasGps(p) && executedTerrain(p)).length;
    const gpsConformesCarte = passages.filter((p) => hasGps(p) && !p.isSuspect).length;
    const anomaliesGps = passages.filter((p) => p.isSuspect).length;
    const planifiesSansTrace = passages.filter((p) => !executedTerrain(p)).length;

    return {
      totalPassages,
      avecCoord,
      sansCoord,
      executesAvecCoord,
      gpsConformesCarte,
      anomaliesGps,
      planifiesSansTrace,
      totalTournees: tournees.length,
    };
  }, [passages, tournees.length]);

  const center = useMemo((): [number, number] => {
    if (markers.length === 0) return DEFAULT_CENTER;
    const sumLat = markers.reduce((s, m) => s + m.position[0], 0);
    const sumLng = markers.reduce((s, m) => s + m.position[1], 0);
    return [sumLat / markers.length, sumLng / markers.length];
  }, [markers]);

  return (
    <div className="flex min-h-[calc(100vh-6rem)] flex-col gap-4 p-4 md:p-6">
      <header className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Carte terrain</h1>
          <p className="mt-1 text-sm text-slate-400">
            Passages prévus / exécutés, anomalies GPS (positions issues des relevés passage).
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => void refresh()}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-600 bg-slate-800 px-4 py-2 text-sm text-slate-100 hover:bg-slate-700"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Actualiser
          </button>
          <Link
            to="/admin/historique-passages"
            className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-500"
          >
            Historique passages
          </Link>
        </div>
      </header>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 px-4 py-3">
          <p className="text-[10px] font-bold uppercase text-slate-500">Passages (liste chargée)</p>
          <p className="text-xl font-bold text-white">{stats.totalPassages}</p>
          <p className="mt-1 text-[11px] text-slate-500">Limite API · inclut sans GPS</p>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 px-4 py-3">
          <p className="text-[10px] font-bold uppercase text-slate-500">Planifiés sans exécution</p>
          <p className="text-xl font-bold text-slate-200">{stats.planifiesSansTrace}</p>
          <p className="mt-1 text-[11px] text-slate-500">Non comptés comme relevés terrain</p>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 px-4 py-3">
          <p className="text-[10px] font-bold uppercase text-slate-500">Sans coordonnées GPS</p>
          <p className="text-xl font-bold text-slate-300">{stats.sansCoord}</p>
          <p className="mt-1 text-[11px] text-slate-500">Absents de la carte</p>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 px-4 py-3">
          <p className="text-[10px] font-bold uppercase text-sky-400/90">Exécutés avec point GPS</p>
          <p className="text-xl font-bold text-sky-300">{stats.executesAvecCoord}</p>
          <p className="mt-1 text-[11px] text-slate-500">Terrain réalisé + coordonnées</p>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 px-4 py-3">
          <p className="text-[10px] font-bold uppercase text-emerald-400/90">Points GPS non suspects</p>
          <p className="text-xl font-bold text-emerald-300">{stats.gpsConformesCarte}</p>
          <p className="mt-1 text-[11px] text-slate-500">Sur passages géolocalisés</p>
        </div>
        <div className="rounded-xl border border-rose-900/40 bg-slate-900/60 px-4 py-3">
          <p className="text-[10px] font-bold uppercase text-rose-400/90">Anomalies / GPS suspect</p>
          <p className="text-xl font-bold text-rose-300">{stats.anomaliesGps}</p>
          <p className="mt-1 text-[11px] text-slate-500">Marqués suspects (toute la liste)</p>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 px-4 py-3">
          <p className="text-[10px] font-bold uppercase text-slate-500">Marqueurs sur carte</p>
          <p className="text-xl font-bold text-cyan-300">{markers.length}</p>
          <p className="mt-1 text-[11px] text-slate-500">≠ passages chargés (sans GPS exclus)</p>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 px-4 py-3">
          <p className="text-[10px] font-bold uppercase text-slate-500">Tournées (liste)</p>
          <p className="text-xl font-bold text-violet-300">{stats.totalTournees}</p>
        </div>
      </div>

      {markers.length === 0 && !loading ? (
        <div className="flex flex-1 flex-col items-center justify-center rounded-2xl border border-dashed border-slate-700 bg-slate-900/40 px-6 py-16 text-center">
          <Layers className="mb-4 h-12 w-12 text-slate-600" />
          <p className="max-w-md text-lg font-medium text-slate-300">
            La carte terrain sera alimentée par les passages, tournées et validations GPS.
          </p>
          <p className="mt-2 max-w-lg text-sm text-slate-500">
            Aucune coordonnée de passage (`startLatitude` / `endLatitude`) dans les enregistrements chargés — les
            passages sans trace GPS n’apparaissent pas sur la carte mais restent visibles dans les compteurs « sans
            coordonnées ».
          </p>
          <Link
            to="/admin/interventions"
            className="mt-6 rounded-xl bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-sky-500"
          >
            Exécution des passages
          </Link>
        </div>
      ) : (
        <div className="grid flex-1 gap-4 lg:grid-cols-[1fr_280px]">
          <div className="min-h-[420px] overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/30 shadow-inner">
            <MapContainer center={center} zoom={13} scrollWheelZoom className="h-[min(70vh,560px)] w-full z-0">
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {markers.map((m) => (
                <CircleMarker key={m.key} center={m.position} radius={9} pathOptions={{ color: m.color, fillColor: m.color, fillOpacity: 0.65 }}>
                  <Tooltip direction="top">{m.title}</Tooltip>
                  <Popup>
                    <div className="text-sm">
                      <strong>{m.title}</strong>
                      <p className="mt-1 text-xs text-slate-600">{m.detail}</p>
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>
          <aside className="space-y-3 rounded-2xl border border-slate-800 bg-slate-900/50 p-4 text-sm text-slate-300">
            <p className="flex items-center gap-2 font-semibold text-white">
              <AlertTriangle className="h-4 w-4 text-amber-400" />
              Légende
            </p>
            <ul className="space-y-2 text-xs">
              <li>
                <span className="mr-2 inline-block h-3 w-3 rounded-full bg-sky-500" /> Passage planifié / en cours
              </li>
              <li>
                <span className="mr-2 inline-block h-3 w-3 rounded-full bg-emerald-500" /> Exécuté ou en validation
              </li>
              <li>
                <span className="mr-2 inline-block h-3 w-3 rounded-full bg-rose-500" /> Suspect / anomalie GPS
              </li>
            </ul>
            <p className="border-t border-slate-800 pt-3 text-xs text-slate-500">
              Les clients ne sont pas géocodés ici tant que l’API ne fournit pas de lat/long client — seuls les points
              passage sont affichés.
            </p>
            <Link to="/admin/planificateur/tournees" className="block text-xs font-medium text-violet-400 hover:underline">
              Ouvrir la liste des tournées →
            </Link>
          </aside>
        </div>
      )}
    </div>
  );
}
